import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";

/* ---------- helpers ---------- */

/* ---------- API ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewCustomer() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lsUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || "",
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw,
    );

  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? "",
  );

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  const [detailRow, setDetailRow] = React.useState(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    name: "",
    tinNumber: "",
    type: "customer",
    phone: "",
    address: "",
  });
  const [saving, setSaving] = React.useState(false);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);

    let apiUrl = "/api/customer";

    // If logged-in user is sales → get only their customers
    if (isSalesMan && currentUserId) {
      apiUrl = `/api/customer/user/${currentUserId}`;
    }

    const { data, error } = await getJSON(apiUrl, { _: Date.now() });

    setLoading(false);

    if (error) {
      setToast({ open: true, msg: "Failed to load customers", sev: "error" });
      setRows([]);
      return;
    }

    const mapped = (Array.isArray(data?.data) ? data.data : []).map((c) => ({
      ...c,
      userFullName: c?.user?.fullName || "-",
    }));

    setRows(mapped);
  }, [isSalesMan, currentUserId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id,
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 90, hide: true },
    { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
    {
      field: "type",
      headerName: "Type",
      width: 140,
      valueGetter: (p) =>
        (p.row.type || "").toString().charAt(0).toUpperCase() +
        (p.row.type || "").toString().slice(1),
    },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: 160 },
    { field: "address", headerName: "Address", flex: 1.3, minWidth: 200 },
    {
      field: "userFullName",
      headerName: "User",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.row.user?.fullName || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditRow(row);
                  setEditValues({
                    name: row.name || "",
                    tinNumber: row.tinNumber || "",
                    type: row.type || "customer",
                    phone: row.phone || "",
                    address: row.address || "",
                  });
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    const payload = {
      name: String(editValues.name ?? "").trim(),
      ...(editValues.tinNumber
        ? { tinNumber: String(editValues.tinNumber).trim() }
        : {}),
      type: String(editValues.type || "customer").trim(),
      phone: String(editValues.phone ?? "").trim(),
      address: String(editValues.address ?? "").trim(),
    };

    const { data, error } = await putJSON(
      `/api/customer/${editRow.id}`,
      payload,
    );
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    const updated = data?.data || data;
    const merged = updated?.id ? updated : { id: editRow.id, ...payload };
    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r)),
    );

    setToast({
      open: true,
      msg: "Customer updated successfully",
      sev: "success",
    });
    setEditOpen(false);
    setEditRow(null);
    load();
  };

  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    const { data, error } = await deleteJSON(`/api/customer/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
    setToast({
      open: true,
      msg: data?.message || "Customer deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);
    load();
  };

  return (
    <>
      <Box m="20px">
        <Header title="CUSTOMERS" subtitle="View, edit, and manage customers" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Customer Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Name" value={detailRow.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Type"
                  value={
                    detailRow.type
                      ? detailRow.type.charAt(0).toUpperCase() +
                        detailRow.type.slice(1)
                      : "-"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="TIN Number"
                  value={detailRow.tinNumber ?? "-"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Phone" value={detailRow.phone || "-"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Address" value={detailRow.address || "-"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="User"
                  value={detailRow.user?.fullName || "-"}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={editValues.name}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, name: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="TIN Number"
              value={editValues.tinNumber}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, tinNumber: e.target.value }))
              }
              fullWidth
              variant="filled"
              placeholder="Optional"
            />
            <TextField
              select
              label="Type"
              value={editValues.type}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, type: e.target.value }))
              }
              fullWidth
              variant="filled"
            >
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="supplier">Supplier</MenuItem>
            </TextField>
            <TextField
              label="Phone Number"
              value={editValues.phone}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, phone: e.target.value }))
              }
              fullWidth
              variant="filled"
              placeholder="+2519xxxxxxxx"
            />
            <TextField
              label="Address"
              multiline
              minRows={2}
              value={editValues.address}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, address: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
